import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import i18next from 'i18next';
import type { Booking } from './useRoomBooking';

// Types
interface AdminStats {
  bookings: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    cancelled: number;
  };
  activity: {
    totalActions: number;
    actionsByType: Array<{
      action: string;
      count: number;
    }>;
    topUsers: Array<{
      user: {
        id: string;
        firstName?: string;
        lastName?: string;
        email: string;
        role: string;
      };
      actionsCount: number;
    }>;
  };
}

interface ActivityLog {
  id: string;
  bookingId: string;
  action: 'CREATED' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'UPDATED';
  userId: string;
  metadata?: any;
  createdAt: string;
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    role: string;
  };
  booking: {
    id: string;
    roomId: string;
    userId: string;
    date: string;
    startTime: string;
    endTime: string;
    status: string;
    room: {
      id: string;
      name: string;
      code: string;
    };
    user: {
      id: string;
      firstName?: string;
      lastName?: string;
      email: string;
    };
  };
}

interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: 'USER' | 'OPERATOR' | 'ADMIN' | 'SUPERADMIN';
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

// Query Keys
export const adminBookingKeys = {
  all: ['admin-booking'] as const,
  pending: () => [...adminBookingKeys.all, 'pending'] as const,
  stats: (filters?: { startDate?: string; endDate?: string }) =>
    [...adminBookingKeys.all, 'stats', filters] as const,
  activity: (filters?: {
    page?: number;
    limit?: number;
    action?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }) => [...adminBookingKeys.all, 'activity', filters] as const,
  userActivity: (userId: string, filters?: { page?: number; limit?: number; action?: string }) =>
    [...adminBookingKeys.all, 'user-activity', userId, filters] as const,
  admins: () => [...adminBookingKeys.all, 'admins'] as const,
};

/**
 * Hook to fetch pending bookings (admin only)
 */
export function usePendingBookings(page = 1, limit = 50) {
  return useQuery({
    queryKey: [...adminBookingKeys.pending(), { page, limit }],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: Booking[]; pagination: any }>(
        `/bookings/pending?page=${page}&limit=${limit}`
      );
      return { bookings: data.data, pagination: data.pagination };
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Infinite-scroll hook for pending bookings (admin only)
 */
interface PendingBookingsPage {
  bookings: Booking[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export function usePendingBookingsInfinite(pageSize = 20) {
  return useInfiniteQuery({
    queryKey: [...adminBookingKeys.pending(), 'infinite', { pageSize }],
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<{ success: boolean; data: Booking[]; pagination: any }>(
        `/bookings/pending?page=${pageParam}&limit=${pageSize}`
      );
      return { bookings: data.data, pagination: data.pagination } as PendingBookingsPage;
    },
    initialPageParam: 1 as number,
    getNextPageParam: (lastPage: PendingBookingsPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch admin statistics
 */
export function useAdminStats(filters?: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: adminBookingKeys.stats(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const { data } = await api.get<{ success: boolean; data: AdminStats }>(
        `/admin/stats?${params.toString()}`
      );
      return data.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch system activity feed (admin only)
 */
export function useSystemActivity(filters?: {
  page?: number;
  limit?: number;
  action?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    queryKey: adminBookingKeys.activity(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.action) params.append('action', filters.action);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const { data } = await api.get<{
        success: boolean;
        data: ActivityLog[];
        pagination: any;
      }>(`/admin/activity?${params.toString()}`);
      return { logs: data.data, pagination: data.pagination };
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch user-specific activity (admin only)
 */
export function useUserActivity(
  userId: string,
  filters?: { page?: number; limit?: number; action?: string }
) {
  return useQuery({
    queryKey: adminBookingKeys.userActivity(userId, filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.action) params.append('action', filters.action);

      const { data } = await api.get<{
        success: boolean;
        data: { user: User; activity: ActivityLog[]; pagination: any };
      }>(`/admin/users/${userId}/activity?${params.toString()}`);
      return data.data;
    },
    enabled: !!userId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch admin users list
 */
export function useAdminUsers() {
  return useQuery({
    queryKey: adminBookingKeys.admins(),
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: User[] }>('/admin/users/admins');
      return data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Mutation hook to approve a booking
 */
export function useApproveBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingId: string) => {
      const { data } = await api.post<{ success: boolean; data: Booking }>(
        `/bookings/${bookingId}/approve`
      );
      return data.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.pending() });
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.stats() });
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.activity() });
      queryClient.invalidateQueries({ queryKey: ['room-booking', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['room-booking', 'my-bookings'] });

      toast.success(i18next.t('bookings:hooks.toasts.bookingApprovedSuccess'), {
        duration: 3000,
        position: 'top-right',
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || i18next.t('bookings:hooks.toasts.bookingApproveFailed');
      toast.error(message, {
        duration: 4000,
        position: 'top-right',
      });
    },
  });
}

/**
 * Mutation hook to reject a booking
 */
export function useRejectBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: string; reason: string }) => {
      const { data } = await api.post<{ success: boolean; data: Booking }>(
        `/bookings/${bookingId}/reject`,
        { reason }
      );
      return data.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.pending() });
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.stats() });
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.activity() });
      queryClient.invalidateQueries({ queryKey: ['room-booking', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['room-booking', 'my-bookings'] });

      toast.success(i18next.t('bookings:hooks.toasts.bookingRejectedSuccess'), {
        duration: 3000,
        position: 'top-right',
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || i18next.t('bookings:hooks.toasts.bookingRejectFailed');
      toast.error(message, {
        duration: 4000,
        position: 'top-right',
      });
    },
  });
}

/**
 * Mutation hook to bulk approve bookings
 */
export function useBulkApproveBookings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingIds: string[]) => {
      const { data } = await api.post<{
        success: boolean;
        data: { approved: number; bookingIds: string[] };
      }>('/bookings/bulk-approve', { bookingIds });
      return data.data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.pending() });
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.stats() });
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.activity() });
      queryClient.invalidateQueries({ queryKey: ['room-booking', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['room-booking', 'my-bookings'] });

      toast.success(i18next.t('bookings:hooks.toasts.bulkApproveSuccess', { count: data.approved }), {
        duration: 3000,
        position: 'top-right',
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || i18next.t('bookings:hooks.toasts.bulkApproveFailed');
      toast.error(message, {
        duration: 4000,
        position: 'top-right',
      });
    },
  });
}

/**
 * Mutation hook to update user role (admin only)
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: 'USER' | 'OPERATOR' | 'ADMIN' | 'SUPERADMIN' }) => {
      const { data } = await api.patch<{ success: boolean; data: User }>(
        `/admin/users/${userId}/role`,
        { role }
      );
      return data.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: adminBookingKeys.admins() });
      queryClient.invalidateQueries({ queryKey: ['users'] });

      toast.success(i18next.t('bookings:hooks.toasts.userRoleUpdatedSuccess'), {
        duration: 3000,
        position: 'top-right',
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || i18next.t('bookings:hooks.toasts.userRoleUpdateFailed');
      toast.error(message, {
        duration: 4000,
        position: 'top-right',
      });
    },
  });
}

// Export types for use in components
export type { AdminStats, ActivityLog, User };
